import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const COLORS = ["var(--color-cyan)", "var(--color-mint)", "var(--color-lilac)", "var(--color-gold)", "var(--color-pink)"];

/**
 * Stages in a ring, with a light travelling round it.
 *
 * The ring carries the idea by itself — a child seeing a closed loop
 * understands "and then it starts again" without being told, which is the part
 * a paragraph about the water cycle reliably fails to land. The travelling
 * highlight only says which way round to read it.
 */
export function CycleFigure({ figure }: FigureProps<Figure>) {
  const labels = figure.labels ?? [];
  if (labels.length < 3) return null;

  const size = 250;
  const centre = size / 2;
  const radius = 88;
  const points = labels.map((label, i) => {
    // Start at the top and go clockwise, which is how a child expects to read a
    // ring — SVG angles start at three o'clock, hence the quarter turn back.
    const angle = (i / labels.length) * 2 * Math.PI - Math.PI / 2;
    return { label, x: centre + radius * Math.cos(angle), y: centre + radius * Math.sin(angle) };
  });

  return (
    <div role="img" aria-label={`A cycle: ${labels.join(", then ")}, and back to the start.`}>
      <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto block h-auto w-full max-w-[300px]">
        <circle
          cx={centre}
          cy={centre}
          r={radius}
          fill="none"
          stroke="rgb(183 155 255 / 0.35)"
          strokeWidth="2"
          strokeDasharray="5 7"
        />
        {/* One dot running the loop: the direction of travel, nothing more. */}
        <circle r="6" fill="var(--color-gold)" className="orbit-dot">
          <animateMotion
            dur={`${labels.length * 1.6}s`}
            repeatCount="indefinite"
            path={`M ${centre} ${centre - radius} A ${radius} ${radius} 0 1 1 ${centre - 0.01} ${centre - radius} Z`}
          />
        </circle>
        {points.map((p, i) => (
          <g key={p.label}>
            <circle cx={p.x} cy={p.y} r="15" fill={COLORS[i % COLORS.length]} />
            <text
              x={p.x}
              y={p.y + 5}
              textAnchor="middle"
              fontSize="14"
              fontWeight="700"
              fill="var(--color-night)"
            >
              {i + 1}
            </text>
          </g>
        ))}
      </svg>

      {/* The labels sit under the ring rather than around it. Text placed at
          arbitrary angles either overlaps or shrinks past readable, and these
          have to survive being read aloud to a six-year-old. */}
      <ol className="mt-1 flex flex-col gap-1">
        {labels.map((label, i) => (
          <li key={label} className="flex items-center gap-2 text-[15px] text-cream/85">
            <span
              className="disp inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[13px] text-night"
              style={{ background: COLORS[i % COLORS.length] }}
            >
              {i + 1}
            </span>
            {label}
          </li>
        ))}
      </ol>
    </div>
  );
}
